import { encodeAttachmentsForApi } from '../../api/attachmentsCodec';
import type { ReturnRequestFormValues, SubmitReturnRequestPayload } from '../../types/vipMp';

function parseWholePositiveInteger(value: string): number | null {
  const trimmed = String(value || '').trim();
  if (!/^[1-9]\d*$/.test(trimmed)) return null;
  return Number(trimmed);
}

const APPROX_USD_RATES: Record<string, number> = {
  USD: 1,
  EUR: 1.09,
  GBP: 1.27,
  JPY: 0.0065,
  AUD: 0.65,
};

export function lineTotalInUsd(values: ReturnRequestFormValues): number {
  const currency = (values.lineItems[0]?.currencyCode || 'USD').toUpperCase();
  const rate = APPROX_USD_RATES[currency] ?? 1;
  const total = values.lineItems.reduce((sum, li) => {
    return sum + (parseFloat(li.unitPrice) || 0) * (parseInt(li.quantity) || 0);
  }, 0);
  return total * rate;
}

export function managerEmailRequired(values: ReturnRequestFormValues): boolean {
  return !!values.notifyCam || lineTotalInUsd(values) > 5000;
}

export function validateStep1(v: ReturnRequestFormValues): string[] {
  const e: string[] = [];
  if (!v.partnerName.trim()) e.push('Partner name is required.');
  if (!v.contactName.trim()) e.push('Contact name is required.');
  if (!v.contactEmail.trim()) e.push('Contact email is required.');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.contactEmail.trim())) e.push('Invalid contact email.');
  if (!/^16\d{8}$/.test(v.orderNumber.trim())) e.push('Order number must be 10 digits starting with 16.');
  if (v.replacementOrder.trim() && !/^16\d{8}$/.test(v.replacementOrder.trim())) {
    e.push('Replacement order must be empty or 10 digits starting with 16.');
  }
  if (!v.orderDate) e.push('Order date is required.');
  return e;
}

export function validateStep2(v: ReturnRequestFormValues): string[] {
  const e: string[] = [];
  if (!/^1005\d{6}$/.test(v.accountId.trim())) e.push('Account ID must be 10 digits starting with 1005.');
  if (!v.customerName.trim()) e.push('Customer name is required.');
  if (!v.geoCode) e.push('GEO is required.');
  if (!v.returnTypeCode) e.push('Return type is required.');
  if (!v.reasonCode) e.push('Reason is required.');
  if (!v.timeframeCode) e.push('Time frame is required.');
  if (!v.managerName.trim()) e.push('Adobe Account Manager name is required.');
  if (v.managerEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.managerEmail.trim())) {
    e.push('Invalid manager email.');
  }
  if (managerEmailRequired(v) && !v.managerEmail.trim()) {
    e.push('Manager email is required when "Notify account manager" is selected.');
  }
  return e;
}

export function validateStep3(v: ReturnRequestFormValues): string[] {
  const e: string[] = [];
  if (!v.lineItems.length) e.push('At least one line item is required.');
  v.lineItems.forEach((li, i) => {
    if (!li.productDescription.trim()) e.push(`Line ${i + 1}: product description is required.`);
    if (!li.adobeSku.trim()) e.push(`Line ${i + 1}: SKU is required.`);
    const price = parseFloat(li.unitPrice);
    if (!Number.isFinite(price) || price < 0.01) e.push(`Line ${i + 1}: price must be at least 0.01.`);
    const qty = parseWholePositiveInteger(li.quantity);
    if (qty == null || qty < 1) e.push(`Line ${i + 1}: quantity must be a whole number >= 1.`);
  });
  const cur = v.lineItems[0]?.currencyCode;
  if (cur && v.lineItems.some((x) => x.currencyCode !== cur)) {
    e.push('All line items must use the same currency as the first line.');
  }
  return e;
}

export function validateStep4(v: ReturnRequestFormValues): string[] {
  const e: string[] = [];
  if (!v.feeAcknowledged) e.push('Fee acknowledgment is required.');
  if (!v.accuracyConfirmed) e.push('Accuracy confirmation is required.');
  return e;
}

export async function buildApiPayload(
  values: ReturnRequestFormValues
): Promise<SubmitReturnRequestPayload> {
  const firstCur = values.lineItems[0]?.currencyCode || 'USD';
  const lineItems = values.lineItems
    .filter((li) => li.productDescription.trim() || li.adobeSku.trim())
    .map((li, index) => {
      const qty = parseWholePositiveInteger(li.quantity);
      if (qty == null) {
        throw new Error(`Line ${index + 1}: quantity must be a whole number >= 1.`);
      }
      return {
        productDescription: li.productDescription.trim(),
        adobeSku: li.adobeSku.trim(),
        deployToId: li.deployToId.trim() || undefined,
        unitPrice: parseFloat(li.unitPrice) || 0,
        quantity: qty,
        currencyCode: firstCur
      };
    });

  const encoded = await encodeAttachmentsForApi(values.attachments);

  return {
    partnerName: values.partnerName.trim(),
    submitterName: values.contactName.trim(),
    submitterEmail: values.contactEmail.trim(),
    adobeSalesOrderNumber: values.orderNumber.trim(),
    replacementOrderNumber: values.replacementOrder.trim() || null,
    orderDate: values.orderDate || null,
    customerMarketplaceAccountId: values.accountId.trim(),
    customerName: values.customerName.trim(),
    geoCode: values.geoCode,
    returnTypeCode: values.returnTypeCode,
    reasonCode: values.reasonCode,
    timeframeCode: values.timeframeCode,
    managerName: values.managerName.trim() || null,
    managerEmail: values.managerEmail.trim() || null,
    notifyCam: values.notifyCam,
    feeAcknowledged: values.feeAcknowledged,
    partnerComments: values.partnerComments.trim() || null,
    lineItems,
    attachments: encoded
  };
}

export function newLineItem(id: string, currencyCode = 'USD'): ReturnRequestFormValues['lineItems'][number] {
  return {
    id,
    productDescription: '',
    adobeSku: '',
    deployToId: '',
    unitPrice: '',
    quantity: '1',
    currencyCode
  };
}

export function defaultFormValues(): ReturnRequestFormValues {
  return {
    partnerName: '',
    contactName: '',
    contactEmail: '',
    orderNumber: '',
    replacementOrder: '',
    orderDate: '',
    accountId: '',
    customerName: '',
    geoCode: '',
    returnTypeCode: '',
    reasonCode: '',
    timeframeCode: '',
    managerName: '',
    managerEmail: '',
    notifyCam: false,
    lineItems: [newLineItem('1')],
    attachments: [],
    partnerComments: '',
    feeAcknowledged: false,
    accuracyConfirmed: false
  };
}
